import type { LoginLimitStatus, LoginLimitType } from './login-limit-api';

type Report = NonNullable<LoginLimitStatus['report']>;

export interface ReportWaitInput {
  status: LoginLimitStatus | null;
  savedType: LoginLimitType | null;
  /** Connect or Check again succeeded on this page, so a billing report is awaited. */
  connectedHere: boolean;
  /** Polling for the billing report ran its full 4 minutes. */
  reportWaitOver: boolean;
  /**
   * `reportKey()` of the report when Check again was selected. That same report does not end the
   * new wait: only a report the new check started or finished does.
   */
  retryBaseline?: string | null;
}

export interface ReportWaitView {
  connected: boolean;
  /** Not connected. A failed or unclear report, or one still pending after 4 minutes, leaves the settings editable. */
  locked: boolean;
  /**
   * The license model check is running (after Connect, Check again, or while the gate reads a first
   * model), so every setting waits for it. Turn off and the connection controls stay usable.
   */
  checkLocked: boolean;
  checking: boolean;
  /** Settings polls the status every 10 seconds while this is true, for up to 4 minutes. */
  waitingForReport: boolean;
  /** After the 4-minute wait, Settings keeps a slow poll so a late model still fills in. */
  slowPoll: boolean;
  detectedType: 'concurrent' | 'named' | null;
  /** Limit type waits for the first billing report and shows that it is checking. */
  typeChecking: boolean;
  typeHelpKey: string;
  /** The report is not available, so Settings offers Check again. */
  canCheckAgain: boolean;
  /** When the gate tries the report again on its own, if it said. */
  retryAt: string | null;
}

/** What identifies one result of the report, so a new check can be told from the old one. */
export function reportKey(report: Report | null | undefined): string | null {
  return report
    ? JSON.stringify([report.state, report.model, report.updatedAt, report.delayed, report.retryAt])
    : null;
}

/** How the billing report shapes the Login Limit section: the lock, the polling, and Limit type. */
export function loginLimitReportWait({
  status,
  savedType,
  connectedHere,
  reportWaitOver,
  retryBaseline = null,
}: ReportWaitInput): ReportWaitView {
  const connected = status?.connection === 'connected';
  const report = status?.report ?? null;
  const checking = report?.state === 'in_progress';
  const settled = (report?.state === 'complete' || report?.state === 'unavailable' || report?.delayed === true)
    && (retryBaseline === null || reportKey(report) !== retryBaseline);
  // Right after Connect the gate may not have started the report yet, so that wait polls too.
  const waitingForReport = !reportWaitOver
    && (checking || (connectedHere && connected && !report?.model && !settled));
  /**
   * A billing report that names one model sets the limit type. Hourly Logged In usage names are
   * unconfirmed and may read as named, so an hourly warning or a saved hours budget keeps the choice open.
   */
  const detectedType = connected && report?.model && !report.hourlyLoggedInWarning && savedType !== 'hours'
    ? report.model
    : null;
  // After 4 minutes, a failed report, or a report without one model, Limit type is a free choice again.
  const typeChecking = connected
    && !detectedType
    && !reportWaitOver
    && !settled
    && !report?.model
    && !report?.hourlyLoggedInWarning
    && savedType !== 'hours'
    && (connectedHere || (checking && savedType === null));
  const reportMissing = connected
    && !report?.model
    && !waitingForReport
    && (reportWaitOver || report?.delayed === true || report?.state === 'unavailable');
  const typeHelpKey = typeChecking
    ? 'login-limit-checking'
    : detectedType
      ? `login-limit-type-detected-${detectedType}`
      : !connected
        ? 'login-limit-type-help-connect'
        : report?.state === 'complete' && !report.model && !report.hourlyLoggedInWarning
          ? report.mixed ? 'login-limit-type-help-mixed' : 'login-limit-type-help-unknown'
          : reportMissing
            ? 'login-limit-type-help-no-report'
            : 'login-limit-type-help';
  return {
    connected,
    locked: !connected,
    checkLocked: connected && waitingForReport && !report?.model,
    checking,
    waitingForReport,
    slowPoll: reportMissing,
    detectedType,
    typeChecking,
    typeHelpKey,
    canCheckAgain: typeHelpKey === 'login-limit-type-help-no-report',
    retryAt: connected && report?.delayed && report.retryAt ? report.retryAt : null,
  };
}
